<template>
  <v-stepper-window-item value="1">
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    ></v-progress-linear>
    <div class="pa-4">
      <v-row>
        <v-col cols="12">
          <v-card 
            title="Schedule Selection" 
            subtitle="Select or create a schedule to update" 
            variant="elevated"
            elevation="3"
          >
            <v-card-text>
              <!-- Schedule Selector -->
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="selectedScheduleLocal"
                    :items="sortedSchedules"
                    item-title="title"
                    :return-object="true"
                    label="Select Schedule"
                    clearable
                    :menu-props="{ maxHeight: 400 }"
                    v-model:search="search"
                    :filter="customFilter"
                    hide-selected
                    :multiple="false"
                    @update:model-value="handleScheduleSelection"
                  />
                </v-col>
              </v-row>

              <!-- Tasks Alert -->
              <v-banner
                v-if="containsTasks"
                color="primary"
              >
                <span class="mr-1">Tasks:</span>
                <v-chip color="primary" class="mr-2">
                  {{ taskCounts.tasks }}
                </v-chip>
              </v-banner>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="selectedScheduleLocal && !loading">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-title>
                <div class="d-flex flex-column">
                  <div class="text-h6">Export Spreadsheet</div>
                </div>
              </v-expansion-panel-title>
              
              <v-expansion-panel-text>

                <v-alert
                  text="Choose which fields to include as columns."
                 
                  type="info"
                  variant="outlined"
                  density="compact"
                ></v-alert>

                <!-- Update Options: switch list -->
                <v-row class="mt-2"> 
                  <v-col cols="12" class="py-0"> 
                    <v-list
                      class="mx-auto ga-4 d-flex flex-column"
                      color="primary"
                      lines="two"
                      select-strategy="multiple"
                      v-model:selected="selectedUpdateOptions"
                    >
                      <!-- Tasks Option -->
                      <v-list-item
                        rounded="lg"
                        class="mb-2"
                        value="tasks"
                        :ripple="false"
                      >
                        <div class="d-flex">
                          <div class="checkbox-container">
                            <v-checkbox
                              :model-value="true"
                              disabled
                              density="compact"
                              color="primary"
                              hide-details
                              @click.stop
                            ></v-checkbox>
                          </div>
                          <div class="flex-grow-1">
                            <div class="text-body-2">
                              <strong>Tasks</strong>
                            </div>
                            <div class="text-medium-emphasis">
                              Choose the amount of Task columns to output in your spreadsheet
                            </div>
                            <div @click.stop class="mt-4">
                              <v-row>
                                <v-col cols="12">
                                  <v-text-field
                                    v-model.number="defaultTaskCount"
                                    type="number"
                                    label="Task Columns"
                                    density="compact"
                                    @blur="clampTaskCount"
                                  />
                                  <div
                                    v-if="taskColumnsExceedRequired"
                                    class="text-caption text-warning mt-n2"
                                  >
                                    Only {{ computedTasksMinDepth }} task column{{ computedTasksMinDepth === 1 ? '' : 's' }} {{ computedTasksMinDepth === 1 ? 'is' : 'are' }} required for the current options — you can reduce the column count to {{ computedTasksMinDepth }} if you want.
                                  </div>
                                  <div
                                    v-if="exceedsExcelGroupingLevels"
                                    class="text-caption text-medium-emphasis"
                                    :class="taskColumnsExceedRequired ? 'mt-1' : 'mt-n2'"
                                  >
                                    This schedule is more than 8 levels deep, so Excel row grouping will be left out of the spreadsheet — Excel supports a maximum of 8 grouping levels. All tasks are still included.
                                  </div>
                                </v-col>
                              </v-row>
                            </div>
                          </div>
                        </div>
                      </v-list-item>

                      <!-- Dates Option -->
                      <v-list-item
                        v-if="usesDates && timeRangeTemplates.length > 0"
                        rounded="lg"
                        class="mb-2"
                        value="dates"
                        :ripple="false"
                      >
                        <div class="d-flex">
                          <div class="checkbox-container">
                            <v-checkbox
                              :model-value="selectedUpdateOptions.includes('dates')"
                              density="compact"
                              color="primary"
                              hide-details
                              @click.stop
                              @change="toggleOption('dates')"
                            ></v-checkbox>
                          </div>
                          <div class="flex-grow-1">
                            <div class="text-body-2">
                              <strong>Dates</strong>
                            </div>
                            <div class="text-medium-emphasis">
                              Choose a time range and update the start, end and duration of each task.
                            </div>
                            <div @click.stop class="mt-4">
                              <v-radio-group
                                v-if="containsTasks && timeRangeTemplates.length > 0 && usesDates && selectedUpdateOptions.includes('dates')"
                                v-model="selectedTimeRangeTemplateId"
                                class="mt-0 custom-radio-group"
                                density="compact"
                              >
                                <v-radio
                                  v-for="template in timeRangeTemplates"
                                  :key="template.id"
                                  :label="template.title"
                                  :value="template.id"
                                  class="custom-radio-group"
                                />
                              </v-radio-group>
                            </div>
                          </div>
                        </div>
                      </v-list-item>

                      <!-- Rest of the list items... -->
                      <!-- Sub tasks Option -->
                      <v-list-item
                        v-if="hasTaskSets"
                        rounded="lg"
                        class="mb-2"
                        value="tasksets"
                        :ripple="false"
                      >
                        <div class="d-flex">
                          <div class="checkbox-container">
                            <v-checkbox
                              :model-value="selectedUpdateOptions.includes('tasksets')"
                              density="compact"
                              color="primary"
                              hide-details
                              @click.stop
                              @change="toggleOption('tasksets')"
                            ></v-checkbox>
                          </div>
                          <div class="flex-grow-1">
                            <div class="text-body-2">
                              <strong>Task Sets</strong>
                            </div>
                            <div class="text-medium-emphasis">
                              Include task sets in spreadsheet
                            </div>
                            <div @click.stop class="mt-4">
                              <v-radio-group
                                v-if="selectedUpdateOptions.includes('tasksets')"
                                v-model="taskSetExportMode"
                                density="compact"
                                class="mt-0"
                              >
                                <v-radio value="all">
                                  <template #label>
                                    <div>
                                      <div class="font-weight-medium">Include task set tasks</div>
                                      <div class="text-caption text-medium-emphasis">Export all tasks including task set children</div>
                                    </div>
                                  </template>
                                </v-radio>
                                <v-radio value="parentOnly" class="mt-2">
                                  <template #label>
                                    <div>
                                      <div class="font-weight-medium">Include only parent task</div>
                                      <div class="text-caption text-medium-emphasis">Export only parent task with task set pre-selected</div>
                                    </div>
                                  </template>
                                </v-radio>
                              </v-radio-group>
                            </div>
                          </div>
                        </div>
                      </v-list-item>

                      <!-- Viewable Locations Option -->
                      <v-list-item
                        rounded="lg"
                        class="mb-2"
                        value="viewableLocations"
                        :ripple="false"
                      >
                        <template #prepend>
                          <v-checkbox
                            :model-value="selectedUpdateOptions.includes('viewableLocations')"
                            density="compact"
                            color="primary"
                            hide-details
                            @click.stop
                            @change="toggleOption('viewableLocations')"
                          ></v-checkbox>
                        </template>
                        <template #title>
                          <div class="d-flex justify-space-between align-center text-body-2">
                            <strong>Viewable Locations</strong>
                          </div>
                        </template>
                        <template #subtitle>
                          <div class="text-medium-emphasis">
                            Include viewable locations in spreadsheet
                          </div>
                          <div @click.stop>
                            <v-text-field
                              v-if="selectedUpdateOptions.includes('viewableLocations')"
                              v-model.number="viewableLocationsDepth"
                              type="number"
                              :min="computedMinDepth"
                              label="Viewable Location Columns"
                              density="compact"
                              color="primary"
                              style="margin-top:15px!important"
                              @blur="clampLocationsDepth"
                            />
                          </div>
                        </template>
                      </v-list-item>
                      <v-list-item
                        v-if="selectedUpdateOptions.includes('dates') && publishedCalendars.length > 1"
                        rounded="lg"
                        class="mb-2"
                        :ripple="false"
                      >
                        <template #prepend>
                          <v-checkbox
                            v-model="includeCalendarOverride"
                            density="compact"
                            color="primary"
                            hide-details
                            @click.stop
                          ></v-checkbox>
                        </template>
                        <template #title>
                          <div class="d-flex justify-space-between align-center text-body-2">
                            <strong>Calendar Override</strong>
                          </div>
                        </template>
                        <template #subtitle>
                          <div class="text-medium-emphasis">
                            Allow per-task calendar override in spreadsheet
                          </div>
                        </template>
                      </v-list-item>
                    </v-list>
                  </v-col>
                </v-row>

                <!-- Download Button -->
                <div class="d-flex justify-start mt-4">
                  <v-btn
                    prepend-icon="mdi-file-excel"
                    @click="generateSpreadsheet"
                    color="success"
                  >
                    Generate Spreadsheet
                  </v-btn>
                </div>

              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
      <v-row  v-if="selectedScheduleLocal && !loading">
        <v-col>
         <!-- Continue Button -->
         <div class="d-flex justify-end mt-4">
            <v-btn
              color="primary"
              @click="handleContinue"
            >
              Continue
            </v-btn>
          </div>
        </v-col>
      </v-row>
      
    </div>
  </v-stepper-window-item>
</template>

<script setup>
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import { useAppStore } from '@/stores/app';
import { useSettingsStore } from '@/stores/settings';
import { useScheduleStore } from '@/stores/schedule';
import { useTaskStore } from '@/stores/task';
import { useResourceStore } from '@/stores/resource';
import { runProcessingTask } from '@/workers/workerClient';
import { deepToRaw } from '@/utils/deepToRaw';

const appStore = useAppStore();
const settingsStore = useSettingsStore();
const scheduleStore = useScheduleStore();
const taskStore = useTaskStore();
const resourceStore = useResourceStore();

const props = defineProps({
  selectedSchedule: {
    type: Object,
    default: null
  },
  schedules: {
    type: Array,
    required: true
  }
});

const emit = defineEmits([
  'update:selected-schedule',
  'create-schedule',
  'continue',
  'update:selected-time-range-template'
]);

const search = ref('');
const loading = ref(false);
const selectedTimeRangeTemplateId = ref('');
const includeCalendarOverride = ref(false);
const selectedUpdateOptions = ref([]);

// Default task columns count
const defaultTaskCount = ref(3);

// Reactive value for the viewable locations depth override
// It is initially set to the computed minimum (see below)
const viewableLocationsDepth = ref(1);

// Task set export mode
const taskSetExportMode = ref('all');

// Use a local computed version of the selected schedule.
const selectedScheduleLocal = computed({
  get: () => props.selectedSchedule,
  set: (value) => emit('update:selected-schedule', value)
});

// Updated usesDates computed property: check schedule.timeRanges or schedule template's timeRangeTemplates.
const usesDates = computed(() => {
  if (selectedScheduleLocal.value) {
    if (selectedScheduleLocal.value.timeRanges && selectedScheduleLocal.value.timeRanges.length > 0) {
      const allNone = selectedScheduleLocal.value.timeRanges.every(range => range.type === 'none');
      return !allNone;
    } else {
      const template = scheduleStore.scheduleTemplatesObj[selectedScheduleLocal.value.scheduleTemplateId];
      if (template && template.length > 0 && template[0].timeRangeTemplates && template[0].timeRangeTemplates.length > 0) {
        const allNone = template[0].timeRangeTemplates.every(range => range.type === 'none');
        return !allNone;
      }
    }
  }
  return false;
});

// Compute available time range templates based on the schedule template.
const timeRangeTemplates = computed(() => {
  if (
    selectedScheduleLocal.value &&
    scheduleStore.scheduleTemplatesObj[selectedScheduleLocal.value.scheduleTemplateId]
  ) {
    return scheduleStore.scheduleTemplatesObj[selectedScheduleLocal.value.scheduleTemplateId][0].timeRangeTemplates;
  }
  return [];
});

const sortedSchedules = computed(() => {
  return [...props.schedules]
    .filter(schedule => schedule.current === true)
    .sort((a, b) =>
      a.title.toLowerCase().localeCompare(b.title.toLowerCase())
    );
});

const taskCounts = ref({ tasks: 0 });
const containsTasks = computed(() => taskCounts.value.tasks > 0);

function customFilter(item, query, itemText) {
  if (query == null || query === '') return true;
  return itemText.toLowerCase().indexOf(query.toLowerCase()) > -1;
}

async function handleScheduleSelection(schedule) {
  taskCounts.value = { tasks: 0 };
  defaultTaskCount.value = 3; //reset

  if (schedule) {
    let usesDatesValue = false;
    if (schedule.timeRanges && schedule.timeRanges.length > 0) {
      const allNone = schedule.timeRanges.every(range => range.type === 'none');
      usesDatesValue = !allNone;
    } else {
      const template = scheduleStore.scheduleTemplatesObj[schedule.scheduleTemplateId];
      if (template && template.length > 0 && template[0].timeRangeTemplates && template[0].timeRangeTemplates.length > 0) {
         const allNone = template[0].timeRangeTemplates.every(range => range.type === 'none');
         usesDatesValue = !allNone;
      }
    }
    scheduleStore.setScheduleUsesDates(usesDatesValue);

    try {
      loading.value = true;
      const path = `/api/${settingsStore.settings.projectCode}/tasks?scheduleIds=${schedule.id}&nested=true`;

      await appStore.apiClient.get(path, (data) => {
        const tasks = data;
        const scheduleTasksObj = { [schedule.id]: tasks };
        scheduleStore.setScheduleTasksObj(scheduleTasksObj);
        taskStore.setTasks([...tasks]);
        taskCounts.value = countAllTasks(tasks);

        nextTick(() => {
          defaultTaskCount.value = computedTasksMinDepth.value;
        });
      });
    } catch (error) {
      console.error("Error fetching tasks for the selected schedule:", error);
    } finally {
      loading.value = false;
    }
  } else {
    scheduleStore.setScheduleTasksObj({});
    taskStore.setTasks([]);
    scheduleStore.setScheduleUsesDates(false);
    defaultTaskCount.value = 3;
  }
  
  // When the schedule changes, reset the update options.
  selectedUpdateOptions.value = [];
}

function countAllTasks(tasks) {
  let counts = { tasks: 0 };
  function traverseTask(task) {
    counts.tasks++;
    if (task.children) {
      task.children.forEach(traverseTask);
    }
  }
  tasks.forEach(traverseTask);
  return counts;
}

// Helper: recursively compute the maximum depth of an array.
// If the array is empty or not an array, return 1.
function getArrayDepth(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return 1;
  let max = 1;
  for (const item of arr) {
    if (Array.isArray(item)) {
      max = Math.max(max, 1 + getArrayDepth(item));
    }
  }
  return max;
}

// Computed minimum viewable locations depth.
// It iterates through the tasks in the store and returns at least 1.
const computedMinDepth = computed(() => {
  const tasks = taskStore.tasks;
  let maxDepth = 0;
  if (Array.isArray(tasks)) {
    tasks.forEach(task => {
      if (task.viewableLocations) {
        const depth = getArrayDepth(task.viewableLocations);
        maxDepth = Math.max(maxDepth, depth);
      }
    });
  }
  return Math.max(maxDepth, 1);
});

// Mirrors the converter's hasTaskSetProfile check: a task is "part of a task set"
// when it carries a taskSetProfileMeta id that resolves to a known profile.
function taskHasTaskSetProfile(task) {
  const taskSetProfileId = task.meta?.taskSetProfileMeta?.taskSetProfileId;
  if (!taskSetProfileId) return false;
  const profiles = taskStore.getTaskSetProfiles;
  return Array.isArray(profiles) && profiles.some(tsp => tsp.profile?.id === taskSetProfileId);
}

// Compute the number of task-level columns required to render a task subtree.
// When skipTaskSetTasks is true (parentOnly export mode) any task that is part of
// a task set is excluded along with its entire subtree — mirroring the converter,
// which skips these tasks and does not recurse into their children. Returns 0 when
// the task itself is skipped, otherwise the column depth (>= 1).
function calculateTaskDepth(task, skipTaskSetTasks = false) {
  if (skipTaskSetTasks && taskHasTaskSetProfile(task)) {
    return 0;
  }

  let maxChildDepth = 0;
  if (task.children && task.children.length > 0) {
    for (const child of task.children) {
      maxChildDepth = Math.max(maxChildDepth, calculateTaskDepth(child, skipTaskSetTasks));
    }
  }
  return 1 + maxChildDepth;
}

// Minimum task columns required for the current export options. This re-evaluates
// whenever the task sets toggle or its mode changes, since parentOnly mode excludes
// task-set tasks and therefore needs fewer columns.
const computedTasksMinDepth = computed(() => {
  selectedScheduleLocal.value; //reference for re-trigger

  const tasks = taskStore.tasks;
  if (!tasks || tasks.length === 0) {
    return 1;
  }

  const taskSetsEnabled = selectedUpdateOptions.value.includes('tasksets');
  const skipTaskSetTasks = taskSetsEnabled && taskSetExportMode.value === 'parentOnly';

  const depths = tasks.map(task => calculateTaskDepth(task, skipTaskSetTasks));
  return Math.max(1, ...depths);
});

// Excel supports at most 8 row grouping levels, so the converter omits row
// grouping entirely for deeper exports (see ScheduleToSpreadsheetConverter).
// computedTasksMinDepth mirrors the converter's row depth, so it decides
// whether the generated file will actually carry grouping.
const exceedsExcelGroupingLevels = computed(() => {
  return computedTasksMinDepth.value > 8;
});

// True when the user's chosen column count exceeds the minimum currently required.
// In this case we keep their value (it may be an intentional override) but surface a
// hint so they know the count can be safely reduced.
const taskColumnsExceedRequired = computed(() => {
  return Number(defaultTaskCount.value) > computedTasksMinDepth.value;
});

// Check if task sets exist in the project
const hasTaskSets = computed(() => {
  return taskStore.taskSetProfiles && taskStore.taskSetProfiles.length > 0;
});

const publishedCalendars = computed(() => scheduleStore.getCalendars || []);

// Calendar-per-time-range mapping happens in the Upload step once a file is
// chosen (see UploadStep.vue), so this step only selects the schedule.
function handleContinue() {
  emit('continue');
}

// Initialize viewableLocationsDepth with the computed minimum.
viewableLocationsDepth.value = computedMinDepth.value;

const toggleOption = (option) => {
  if (option === 'tasks') return; // Prevent tasks from being toggled
  
  const index = selectedUpdateOptions.value.indexOf(option);
  if (index === -1) {
    selectedUpdateOptions.value.push(option);
  } else {
    selectedUpdateOptions.value.splice(index, 1);
  }
};

// Watch the computed minimum depth. If it increases above the current viewableLocationsDepth,
// update the value.
watch(computedMinDepth, (newVal) => {
  if (viewableLocationsDepth.value < newVal) {
    viewableLocationsDepth.value = newVal;
  }
});

function clampLocationsDepth() {
  if (!viewableLocationsDepth.value || viewableLocationsDepth.value < computedMinDepth.value) {
    viewableLocationsDepth.value = computedMinDepth.value;
  }
}

watch(computedTasksMinDepth, (newMinDepth) => {
  if (defaultTaskCount.value < newMinDepth) {
    defaultTaskCount.value = newMinDepth;
  }
});

function clampTaskCount() {
  if (!defaultTaskCount.value || defaultTaskCount.value < computedTasksMinDepth.value) {
    defaultTaskCount.value = computedTasksMinDepth.value;
  }
}

watch(selectedUpdateOptions, (newVal) => {
  if (!newVal.includes('tasks')) {
    selectedUpdateOptions.value.push('tasks');
  }
}, { deep: true });

watch(() => props.selectedSchedule, async (newSchedule) => {
  if (newSchedule) {
    // Wait for the task data to be processed
    await nextTick();
    // Then force reset the defaultTaskCount to the new minimum
    defaultTaskCount.value = computedTasksMinDepth.value;
  } else {
    defaultTaskCount.value = 1;
  }
}, { deep: true });


const generateSpreadsheet = async () => {
  // Deep clone tasks to avoid any modifications to the store during export
  const tasks = JSON.parse(JSON.stringify(taskStore.tasks));
  const updateOptions = {
    defaultTaskCount: defaultTaskCount.value,
    dates: selectedUpdateOptions.value.includes('dates'),
    tasksets: selectedUpdateOptions.value.includes('tasksets'),
    taskSetExportMode: taskSetExportMode.value,
    viewableLocations: selectedUpdateOptions.value.includes('viewableLocations'),
    calendars: publishedCalendars.value,
    // Fallback for override cells whose task calendarId can't be resolved.
    timeRangeCalendars: selectedScheduleLocal.value?.timeRangeCalendars || [],
    includeCalendarOverride: includeCalendarOverride.value
  };

  if (updateOptions.tasksets) {
    updateOptions.taskSetProfiles = taskStore.taskSetProfiles;
  }

  if (updateOptions.viewableLocations) {
    updateOptions.maxViewableLocationsDepth = Math.max(viewableLocationsDepth.value, computedMinDepth.value);
    updateOptions.locationList = resourceStore.locationsList;
  }

  try {
    loading.value = true;
    // Workbook generation is CPU-bound (exceljs serialization) — run it in
    // the processing worker; deepToRaw strips Vue proxies so the payload can
    // cross the worker boundary.
    const buffer = await runProcessingTask('exportXlsx', deepToRaw({
      tasks,
      selectedTimeRangeId: selectedTimeRangeTemplateId.value,
      updateOptions
    }));

    // Convert buffer to base64
    const base64 = arrayBufferToBase64(buffer);
    
    // Prepare filename
    const filename = `${selectedScheduleLocal.value.title}_tasks.xlsx`;
    
    // Prepare data for server request
    const requestData = {
      excelBuffer: base64,
      filename: filename
    };
    
    // Step 1: Generate token
    const tokenResponse = await fetch('https://paginated-report-service.rdrive.io/api/excel/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    });
    
    if (!tokenResponse.ok) {
      throw new Error(`Server responded with ${tokenResponse.status}: ${tokenResponse.statusText}`);
    }
    
    // Get the token from response
    const tokenData = await tokenResponse.json();
    
    if (!tokenData.token) {
      throw new Error('No token received from server');
    }
    
    // Step 2: Use token to download the file
    // Create a hidden iframe to handle the download to avoid CORS issues
    const downloadUrl = `https://paginated-report-service.rdrive.io/api/excel/download?token=${tokenData.token}`;
    
    // Option 1: Use iframe for download (better for handling in-browser)
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = downloadUrl;
    document.body.appendChild(iframe);
    
    // Remove iframe after download has started (typically after a short delay)
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 2000);
    
    // Option 2 (Alternative): Direct window navigation for more reliable downloads
    // window.location.href = downloadUrl;
    
  } catch (error) {
    console.error("Error processing or downloading tasks", error);
  } finally {
    loading.value = false;
  }
};

// Define the arrayBufferToBase64 function
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  
  return btoa(binary);
}

watch(timeRangeTemplates, (newTemplates) => {
  if (newTemplates.length > 0) {
    const defaultTemplate = newTemplates.find(template => template.isDefault);
    selectedTimeRangeTemplateId.value = defaultTemplate ? defaultTemplate.id : newTemplates[0].id;
  }
}, { immediate: true });

watch(selectedTimeRangeTemplateId, (newTemplateId) => {
  const selectedTemplate = timeRangeTemplates.value.find(template => template.id === newTemplateId);
  emit('update:selected-time-range-template', selectedTemplate);
});

watch(usesDates, (newUsesDates) => {
  if (!newUsesDates) {
    selectedTimeRangeTemplateId.value = '';
    emit('update:selected-time-range-template', null);
  }
});

onMounted(() => {
  if (!selectedUpdateOptions.value.includes('tasks')) {
    selectedUpdateOptions.value.push('tasks');
  }
});
</script>


<style scoped>
.v-list-item--active {
  /* Override the active state styling */
  background-color: transparent !important;
  /* v-list color="primary" tints selected rows' text via .text-primary. The
     dark theme's primary is a muted grey (#AAB2B6), so selected options read
     as disabled. Keep full-emphasis text, matching the transparent background
     above. */
  color: rgb(var(--v-theme-on-surface)) !important;
}

/* Radio/checkbox labels here wrap the option's primary content, so they
   shouldn't carry Vuetify's medium-emphasis label opacity. Nested
   .text-medium-emphasis captions still dim themselves. */
:deep(.v-selection-control .v-label) {
  opacity: 1;
}

.v-list-item--active:hover {
  background-color: rgba(var(--v-theme-primary), 0.05) !important;
}

/* Keep the hover effect for the list items */
.v-list-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.05) !important;
}
</style>